import styled from 'styled-components'
import { screenSizes } from '@shipt/design-system-themes'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { Body, Display } from '@shipt/design-system-typography'
import { type RetailerHeroData } from '@/features/cms/components/CMS/types'
import { ReferenceRenderer } from '@/features/cms/components/CMS/ReferenceRenderer'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { spacing } from '@/features/shared/theme/tokens'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { theme } from '@/features/shared/constants/shiptTheme'

/**
 * This component corresponds to the `retailer_hero` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/retailer_hero
 * - **Production** - https://cms.shipt.com/content-type/pool/retailer_hero
 */
export const RetailerHero = ({
  content_type_id,
  id,
  data: {
    background_color,
    heading,
    hide_image_for_mobile,
    image,
    references,
    retailer_logo,
    subheading,
  },
}: RetailerHeroData) => {
  try {
    return (
      <Wrapper>
        <Container
          backgroundColor={background_color}
          {...getCMSWrapperProps({ content_type_id, id })}
        >
          {!!image?.src && (
            <ImageContainer hide_image_for_mobile={hide_image_for_mobile}>
              <HeroImage alt={image.alt} src={image.src} width={528} height={440} preload />
            </ImageContainer>
          )}
          <CopyContainer>
            {!!retailer_logo?.src && (
              <LogoWrapper>
                <Image
                  src={retailer_logo.src}
                  alt={retailer_logo.alt}
                  width={retailer_logo.width}
                  height={retailer_logo.height}
                  style={{
                    width: 'auto',
                    height: 'auto',
                    minWidth: 113,
                  }}
                  preload
                />
              </LogoWrapper>
            )}
            <Heading size="sm" as="h2">
              {heading}
            </Heading>
            <SubHeading>{subheading}</SubHeading>
            <ReferenceRenderer references={references} />
          </CopyContainer>
        </Container>
      </Wrapper>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const Wrapper = styled.section`
  margin-bottom: ${spacing(72)};
  width: 100%;
  margin-top: ${spacing(-20)};

  @media ${screenSizes.tablet} {
    padding: ${spacing(0, 'lg')};
  }

  @media ${screenSizes.smDesktop} {
    padding-right: ${spacing(40)};
    padding-left: ${spacing(40)};
  }
`
const Container = styled.div<{ backgroundColor?: string }>`
  margin: ${spacing(0, 'auto')};
  max-width: 67.5rem;
  width: 100%;
  padding: ${spacing('lg', 'md')};
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${({ backgroundColor }) => backgroundColor || theme.green100};
  justify-content: center;

  @media ${screenSizes.tablet} {
    border-radius: 16px;
    flex-direction: row;
    margin-top: ${spacing('lg')};
  }
`

const ImageContainer = styled.div<{ hide_image_for_mobile?: boolean }>`
  order: 1;
  max-width: 23.4375rem;
  align-items: center;
  margin: ${spacing(0, 'auto')};
  display: ${({ hide_image_for_mobile }) => (hide_image_for_mobile ? 'none' : 'flex')};

  @media ${screenSizes.tablet} {
    display: flex;
    margin-right: ${spacing(0)};
    margin-left: ${spacing(0)};
    width: 50%;
    max-width: 50%;
    flex-shrink: 2;
  }
`

const HeroImage = styled(Image)`
  max-height: 100%;
  flex: 1;
  max-width: 23.4375rem;
  width: 100%;
  height: auto;

  @media ${screenSizes.tablet} {
    max-width: 37.5rem;
  }
`

const CopyContainer = styled.div`
  order: 1;
  align-items: center;
  display: flex;
  flex-direction: column;
  text-align: center;
  max-width: 21.4375rem;
  justify-content: center;
  margin: ${spacing(0, 'auto')};

  @media ${screenSizes.tablet} {
    order: 2;
    padding: 0;
    max-width: 50%;
    margin-right: ${spacing(0)};
    margin-left: ${spacing(0)};
    width: 100%;
    padding-left: ${spacing('md')};

    .address-input-wrapper {
      justify-content: center;
    }
  }
`

const LogoWrapper = styled.div`
  margin-bottom: ${spacing('lg')};
  max-width: 12rem;
  max-height: 12rem;
`

const Heading = styled(Display)`
  margin: ${spacing(0, 'auto', 'lg')};
  max-width: 25rem;
  white-space: pre-line;
`

const SubHeading = styled(Body)`
  max-width: 21.5rem;
  margin: ${spacing(0, 'auto', 'xxl')};
`
